import React from 'react';
import PropTypes from 'prop-types';

import {Box, CircularProgress, Divider, Stack} from 'views/components';
import {Tab, TabContext, TabList, TabPanel} from 'views/components';
import {translate} from 'views/module';

import CaregiverHeader from './header/CaregiverHeader';
import SlashCommands from './slash_commands/SlashCommands';
import Correspondances from './correspondances/Correspondances';
import Schedule from './schedule/Schedule';
import ClientConnection from './client_connection/ClientConnection';
import ClientChats from './caregiver_chat/ClientChats';
import Payment from './payment/Payment';
import Biography from './biography/Biography';
import Case from './case/Case';
import Review from './review/Review';
import Interviews from './interviews/Interviews';
import PlaybookView from './playbooks/PlaybookView';
import Tasks from './tasks/TaskList';
import TaskOverview from './tasks/TaskOverview';

const CaregiverFeed = (props) => {
  const {
    feedTab,
    setFeedTab,
  } = props;
  const {
    caregiverTasksFilter,
    setCaregiverTasksFilter,
  } = props.caregiverTasksFilterOptions;
  const [shouldScrollToTasks, setShouldScrollToTasks] = React.useState(false);
  const tasksSectionRef = React.useRef(null);

  const handleChange = (event, newValue) => {
    setFeedTab(newValue);
  };

  const handleViewAllPlaybookTasks = React.useCallback((playbookRn) => {
    setCaregiverTasksFilter(playbookRn);
    setFeedTab('TASKS');
    setShouldScrollToTasks(true);
  }, [setCaregiverTasksFilter, setFeedTab]);

  React.useEffect(() => {
    if (!shouldScrollToTasks || feedTab !== 'TASKS') {
      return undefined;
    }

    const animationFrame = window.requestAnimationFrame(() => {
      tasksSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      setShouldScrollToTasks(false);
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [feedTab, shouldScrollToTasks]);

  return (
    <Stack spacing={2} divider={<Divider />}>
      <CaregiverHeader
        {...props}
      />
      <SlashCommands
        {...props}
      />
      <PlaybookView
        {...props}
        onViewAllPlaybookTasks={handleViewAllPlaybookTasks}
      />
      <TaskOverview
        {...props}
      />
      <Box
        sx={{
          boxSizing: 'border-box',
          width: '100%',
          backgroundColor: '#FFFFFF',
          border: '1px solid #E8E4DE',
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        <TabContext
          value={feedTab}
        >
          <TabList
            onChange={handleChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              label={translate('caregiver.CaregiverDetail.Case.Title')}
              value="CASE"
            />
            <Tab
              label={translate('caregiver.CaregiverDetail.Correspondance.Title')}
              value="CORRESPONDANCE"
            />
            <Tab
              label={translate('caregiver.CaregiverDetail.Biography.Title')}
              value="BIOGRAPHY"
            />
            <Tab
              label={translate('caregiver.CaregiverDetail.Tasks.Title')}
              value="TASKS"
            />
            <Tab
              label={translate('caregiver.CaregiverDetail.Scheduling.Title')}
              value="SCHEDULING"
            />
            <Tab
              label={translate('caregiver.CaregiverDetail.ClientConnections.Title')}
              value="CLIENTS"
            />
            <Tab
              label={translate('caregiver.CaregiverDetail.ClientConnections.Chat.TabTitle')}
              value="CHATS"
            />
            <Tab
              label={translate('caregiver.CaregiverDetail.Payment.Title')}
              value="PAYMENT"
            />
            <Tab
              label={translate('caregiver.CaregiverDetail.Reviews.Title')}
              value="REVIEWS"
            />
            <Tab
              label={translate('caregiver.CaregiverDetail.Interviews.Title')}
              value="INTERVIEWS"
            />
          </TabList>
          <TabPanel
            value={'CORRESPONDANCE'}
            sx={{
              m: 0, p: 0,
            }}
          >
            <Correspondances
              {...props}
            />
          </TabPanel>
          <TabPanel
            value={'BIOGRAPHY'}
            sx={{
              m: 0, p: 0,
            }}
          >
            <Biography
              {...props}
            />
          </TabPanel>
          <TabPanel
            value={'TASKS'}
            sx={{
              m: 0, p: 0,
            }}
          >
            <Box ref={tasksSectionRef}>
              {
                props.caregiverTasksLoading ?
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: '160px',
                    }}
                  >
                    <CircularProgress />
                  </Box> :
                  <Tasks
                    {...props}
                    caregiverTasksFilterOptions={{caregiverTasksFilter, setCaregiverTasksFilter}}
                  />
              }
            </Box>
          </TabPanel>
          <TabPanel
            value={'SCHEDULING'}
            sx={{
              m: 0, p: 0,
            }}
          >
            <Schedule
              {...props}
            />
          </TabPanel>
          <TabPanel
            value={'CLIENTS'}
            sx={{
              m: 0, p: 0,
            }}
          >
            <ClientConnection
              {...props}
            />
          </TabPanel>
          <TabPanel
            value={'CHATS'}
            sx={{
              m: 0, p: 0,
            }}
          >
            <ClientChats
              {...props}
            />
          </TabPanel>
          <TabPanel
            value={'PAYMENT'}
            sx={{
              m: 0, p: 0,
            }}
          >
            <Payment
              {...props}
            />
          </TabPanel>
          <TabPanel
            value={'REVIEWS'}
            sx={{
              m: 0, p: 0,
            }}
          >
            <Review
              {...props}
            />
          </TabPanel>
          <TabPanel
            value={'INTERVIEWS'}
            sx={{
              m: 0, p: 0,
            }}
          >
            <Interviews
              {...props}
            />
          </TabPanel>
          <TabPanel
            value={'CASE'}
            sx={{
              m: 0, p: 0,
            }}
          >
            <Case
              {...props}
            />
          </TabPanel>
        </TabContext>
      </Box>
    </Stack>
  );
};
CaregiverFeed.propTypes = {
  caregiverBackend: PropTypes.object.isRequired,
  caregiverRn: PropTypes.string.isRequired,
  caregiverData: PropTypes.object.isRequired,
  caregiverAuth: PropTypes.object,
  caregiverBioQuestions: PropTypes.object.isRequired,
  caregiverTasksLoading: PropTypes.bool.isRequired,
  feedTab: PropTypes.string.isRequired,
  setFeedTab: PropTypes.func.isRequired,
  caregiverTasksFilterOptions: PropTypes.shape({
    caregiverTasksFilter: PropTypes.string.isRequired,
    setCaregiverTasksFilter: PropTypes.func.isRequired,
  }).isRequired,
};

export default React.memo(CaregiverFeed);
